import React, { useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import BlockIcon from '@mui/icons-material/Block';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import PauseIcon from '@mui/icons-material/Pause';
import { Search } from '@deere/fuel-react/Search';
import { useHistory, useRouteMatch } from 'react-router-dom';
import './PartnerManager.css';
import useTaskQueueEvents from '../../hooks/useTaskQueueEvents';
import { DynamoDBItemMinimal } from './types';
import { TASK_STATES } from './constants';

type TaskTone = 'cancelled' | 'new' | 'success' | 'review';


const TaskQueue: React.FC = () => {
    const history = useHistory();
    const { url } = useRouteMatch();
    const [taskFilter, setTaskFilter] = useState<string>('All');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const { tasks, loading, error, isConnected } = useTaskQueueEvents();

    const taskFilterOptions = useMemo<any[]>(() => {
        const statuses = Array.from(new Set(tasks.map((task: DynamoDBItemMinimal) => (task as any).state)));

        return [
            { label: 'All', value: 'All' },
            ...statuses.map((status) => ({ label: status as string, value: status as string })),
        ];
    }, [tasks]);

    const visibleTasks = useMemo<DynamoDBItemMinimal[]>(() => tasks.filter((task: DynamoDBItemMinimal) => {
        const matchesFilter = taskFilter === 'All' || task.state === taskFilter;
        // Si el searchTerm viene del autocomplete tiene formato "SCTASK123 - Supplier Name"
        // Extraemos solo la parte del SCTASK para comparar
        const normalizedSearch = searchTerm.split(' - ')[0].trim().toLowerCase();

        if (!normalizedSearch) {
            return matchesFilter;
        }

        const requestId = (task.request_id ?? '').toLowerCase();

        return matchesFilter && requestId.includes(normalizedSearch);
    }), [searchTerm, taskFilter, tasks]);

    const statusVariantByTone: Record<TaskTone, string> = {
        cancelled: 'criticalError',
        new: 'informational',
        success: 'success',
        review: 'warning',
    };

    const statusIconByTone: Record<TaskTone, any> = {
        cancelled: <BlockIcon fontSize="small" />,
        new: <fuel-icon name="info" />,
        success: <fuel-icon name="check" />,
        review: <PauseIcon fontSize="small" />,
    };

    const statusSxByTone: Record<TaskTone, any> = {
        cancelled: {
            '& .MuiChip-label': {
                color: '#ffffff',
            },
            '& .MuiChip-icon': {
                color: '#ffffff',
            },
        },
        success: {},
        review: {},
        new: {},
    };

    const getToneFromStatus = (status: string): TaskTone => {
        if (status === TASK_STATES.DRAFT_APPROVED) {
            return 'review';
        }

        if (status === TASK_STATES.RELEASE_COMPLETED) {
            return 'success';
        }

        if (status === TASK_STATES.DRAFT_FAILED || status === TASK_STATES.RELEASE_FAILED) {
            return 'cancelled';
        }

        return 'review';
    };

    const searchOptions = useMemo<any[]>(() => {
        const normalized = searchTerm.trim().toLowerCase();

        // Deduplicate by request_id to avoid repeated suggestions
        const seen = new Set<string>();
        const uniqueTasks = tasks.filter((task: DynamoDBItemMinimal) => {
            const key = task.request_id ?? task.id;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        return uniqueTasks
            .filter((task: DynamoDBItemMinimal) => {
                if (!normalized) return true;
                return (task.request_id ?? '').toLowerCase().includes(normalized);
            })
            .map((task: DynamoDBItemMinimal, index: number) => ({
                label: `${task.request_id ?? ''}`,
                value: `${task.request_id ?? task.id}-${index}`,
            }));
    }, [tasks, searchTerm]);

    return (
        <div className="partner-manager-container">
            <Card className="task-queue-surface">
                <div className="task-queue-shell">
                    <div className="task-queue-header">
                        <span className="task-queue-header-icon" aria-hidden="true">
                            <ChecklistRoundedIcon fontSize="small" />
                        </span>
                        <h1 className="task-queue-title">
                            Task Queue
                        </h1>
                    </div>

                    <div className="task-queue-filters">
                        <Search
                            label="Task search"
                            placeholder="Filter by task name"
                            constraints={taskFilterOptions}
                            options={searchOptions}
                            variant="onWhite"
                            fullWidth
                            slotProps={{
                                constraint: {
                                    value: taskFilter,
                                    onChange: (event: any) => {
                                        setTaskFilter(event.target.value);
                                    },
                                    'aria-label': 'Filter tasks by status',
                                },
                                search: {
                                    inputValue: searchTerm,
                                    onInputChange: (_event: any, value: string | null) => {
                                        setSearchTerm(value || '');
                                    },
                                },
                            }}
                        />
                    </div>

                    {loading && (
                        <div className="task-card task-card-empty">
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                                <CircularProgress />
                                <p className="task-empty-state">Connecting to task queue...</p>
                            </div>
                        </div>
                    )}

                {error && (
                    <Alert severity="error" style={{ margin: '16px' }}>
                        {error.message || 'Failed to connect to task queue'}
                    </Alert>
                )}

                    <div className="task-queue-list">
                        {visibleTasks.map((task: DynamoDBItemMinimal, index: number) => (
                            <button
                                key={`${task.id}-${(task as DynamoDBItemMinimal).state}-${index}`}
                                type="button"
                                className="task-card task-card-button"
                                onClick={() => history.push(`${url.replace('/taskQueue', '/taskDetail')}/${(task as DynamoDBItemMinimal).id}/${encodeURIComponent(task.id)}`)}
                            >
                                <div className="task-card-content">
                                    <div>
                                        <div className="task-id">{task.request_id}</div>
                                        <div className="task-partner">{task.supplier_name}</div>
                                    </div>
                                    <Chip
                                        label={(task as DynamoDBItemMinimal).state}
                                        icon={statusIconByTone[getToneFromStatus((task as DynamoDBItemMinimal).state)]}
                                        variant="badge"
                                        color={statusVariantByTone[getToneFromStatus((task as DynamoDBItemMinimal).state)] as any}
                                        className="task-status"
                                        size="small"
                                        sx={statusSxByTone[getToneFromStatus((task as DynamoDBItemMinimal).state)]}
                                    />
                                </div>
                            </button>
                        ))}

                        {visibleTasks.length === 0 && (
                            <div className="task-card task-card-empty">
                                <p className="task-empty-state">
                                    No tasks match the selected filter.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default TaskQueue;